import { useLocalStorageState, usePrevious } from "ahooks";
import AOS from "aos";
import "aos/dist/aos.css";
import cn from "classnames";
import { isEqual } from "lodash";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";
import { SHOW_COUNT_DOWN_LIMIT } from "@/constants/config";
import Countdown from "react-countdown";
import FlightIcon from "@mui/icons-material/Flight";
import HotelIcon from "@mui/icons-material/Hotel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StarPurple500Icon from "@mui/icons-material/StarPurple500";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";

import { FeedbackBar } from "@/components/FeedbackBar";
import HightLights from "@/components/Highlights";
import ReferralBanner from "@/components/ReferralBanner";
import RoutesRecommend from "@/components/RoutesRecommend";
import SearchBar from "@/components/SearchBar";
import SearchBox from "@/components/SearchBox";
import { ROUTE_NUMBER_COUNT } from "@/constants/config";
import { siteName } from "@/constants/config";
import { MAX_PANORAMA_SEARCH_DAYS } from "@/constants/membership";
import { FREE_FLIGHT_RANGE, MAX_FLIGHT_RANGE } from "@/constants/membership";
import useCheckSearchCondition from "@/hooks/useCheckSearchCondition";
import useEvent from "@/hooks/useEvent";
import useHotelSearchCondition from "@/hooks/useHotelSearchCondition";
import usePlatform from "@/hooks/usePlatform";
import { useQuery } from "@/hooks/useQuery";
import { SearchConditionType } from "@/hooks/useSearchCondition";
import useThemeData from "@/hooks/useThemeData";
import HotelSearchBar from "@/pages/Hotel/components/SearchBar";
import RouteSearchBar from "@/pages/Routes/components/SearchBar";
import useCheckRouteCondition from "@/pages/Routes/hooks/useCheckCondition";
import {
  ConditionType as RoutesConditionType,
  createSearchQuery as createRouteSearchQuery,
} from "@/pages/Routes/hooks/useCondition";
import { createSearchQuery } from "@/utils/searchQuery";

import ReferralProgram from "../Guides/ReferralProgram";
import useCheckCondition from "../Hotel/hooks/useCheckCondition";
import HomeBanner from "./HomeBanner";

AOS.init({
  once: true,
  delay: 50,
  duration: 500,
  easing: "ease-in-out",
});

enum EnumTab {
  Flight = 1,
  Panorama = 2,
  Mega = 3,
  Hotel = 4,
}

const Home = () => {
  const recommendRef = useRef<React.ElementRef<typeof RoutesRecommend>>(null);
  const theme = useThemeData();
  const { checkSearchCondition } = useCheckSearchCondition();
  const { checkCondition: checkRouteCondition } = useCheckRouteCondition();
  const { isPc } = usePlatform();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const [condition, setCondition] = useState<SearchConditionType | null>(null);

  const [routesCondition, setRoutesCondition] = useState<RoutesConditionType | null>(null);

  const { getSearchParams, condition: hotelCondition } = useHotelSearchCondition("home");

  const { checkHotelCondition } = useCheckCondition();

  const query = useQuery<{ tab: string }>();

  const [tab, setTab] = useLocalStorageState("home-search-tab", {
    defaultValue: Number(query.tab) || EnumTab.Flight,
  });

  const preFromLocation = usePrevious(condition?.fromLocation);
  const preFromRoutesLocation = usePrevious(routesCondition?.fromLocation);

  const lightBg = "https://img.awardtool.com/home_flights_light_theme_v2.jpg";
  const darkBg = "https://img.awardtool.com/home_flights_dark_theme_v2.jpg";

  const handleChange = useCallback((data: SearchConditionType) => {
    setCondition(data);
  }, []);

  const search = useCallback(() => {
    if (condition) {
      const errMsg = checkSearchCondition(condition);

      if (errMsg) return;

      const params = createSearchQuery(condition);
      const search = createSearchParams(params).toString();

      setSearchParams(search);

      // 手动出发搜索按钮时缓存搜索条件到本地
      localStorage.setItem("flight_search_conditions", JSON.stringify(condition));

      if (isPc) {
        // window.open(`/flight?${search}`, "_blank");
        navigate({
          pathname: "/flight",
          search,
        });
      } else {
        navigate({
          pathname: "/flight",
          search,
        });
      }
    }
  }, [condition, navigate, setSearchParams, checkSearchCondition, isPc]);

  const handleRoutesChange = useCallback((data: RoutesConditionType) => {
    setRoutesCondition(data);
  }, []);

  const searchRoute = useCallback(() => {
    if (routesCondition) {
      const errMsg = checkRouteCondition(routesCondition);

      if (errMsg) return;

      const params = createRouteSearchQuery(routesCondition);
      const search = createSearchParams(params).toString();

      setSearchParams(search);

      // 手动出发搜索按钮时缓存搜索条件到本地
      localStorage.setItem("routes_search_conditions", JSON.stringify(routesCondition));

      const url =
        localStorage.getItem("show_individual_flights") === "true"
          ? `/routes-flights?${search}`
          : `/routes?${search}`;

      if (isPc) {
        window.open(`${url}`, "_self");
      } else {
        window.open(`${url}`, "_self");
      }
    }
  }, [routesCondition, setSearchParams, checkRouteCondition, isPc]);

  const searchHotel = useEvent(() => {
    const res = checkHotelCondition(hotelCondition);
    if (!res) {
      return;
    }
    const search = getSearchParams(hotelCondition);
    if (isPc) {
      window.open(`/hotels?${search}`, "_blank");
    } else {
      window.open(`/hotels?${search}`, "_self");
    }
  });

  useEffect(() => {
    // Get the url
    const pathName = window.location.pathname;
    // Check if it's referral link
    if (pathName.substring(0, 2) == "/r") {
      // Get the last part of the path
      const pathNameArray = pathName.split("/");

      const referralCode = pathNameArray[pathNameArray.length - 1];
      const referralInfo = {
        referralCode: referralCode,
        expire_time: new Date().getTime() + 1000 * 60 * 60 * 1,
      };
      // Store the referral code in local storage
      localStorage.setItem("referralCode", JSON.stringify(referralInfo));
    }
    // Check if string is in the search string
    const searchStr = window.location.search;
    if (searchStr.includes("success=true&session_id=")) {
      // Change url to remove the query string
      // Get current epoch time
      const epochTime = new Date().getTime();
      localStorage.setItem("update_info", JSON.stringify({ expire_time: epochTime + 1000 * 60 }));

      setTimeout(() => {
        const newUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      }, 1000 * 1);

      // Pro user just reigstered, congrats!
      setOpen(true);
    }

    // Get source id if there is any
    const urlParams = new URLSearchParams(window.location.search);
    const sourceId = urlParams.get("sourceId"); // 'John'
    if (sourceId) {
      localStorage.setItem("trackdesk_source_id", sourceId);
    }
  }, []);

  useEffect(() => {
    if (tab == EnumTab.Flight) {
      if (!isEqual(preFromLocation, condition?.fromLocation)) {
        recommendRef.current?.refresh({
          origin: condition?.fromLocation?.map((item) => item.iata) || [],
        });
      }
    }

    if (tab == EnumTab.Panorama) {
      if (!isEqual(preFromRoutesLocation, routesCondition?.fromLocation)) {
        recommendRef.current?.refresh({
          origin: routesCondition?.fromLocation?.map((item) => item.code) || [],
        });
      }
    }
  }, [
    tab,
    condition?.fromLocation,
    preFromLocation,
    routesCondition?.fromLocation,
    preFromRoutesLocation,
  ]);
  const store = useGlobalStoreContext();
  let beforeCode, afterCode, title, couponCode, expireSec, showPromotion;
  if (store.promotion) {
    const text = store.promotion.text;
    couponCode = store.promotion.code;
    title = store.promotion.title;
    expireSec = (store.promotion.expire - Math.floor(Date.now() / 1000)) * 1000;
    // Split the text into two parts: before the code and after the code
    const index = text.indexOf(couponCode);
    if (index !== -1) {
      beforeCode = text.substring(0, index);
      afterCode = text.substring(index + couponCode.length);
    } else {
      beforeCode = text;
      afterCode = "";
    }
  }
  if (expireSec && expireSec > 0) {
    showPromotion = true;
  } else {
    showPromotion = false;
  }

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>AwardTool | Discover Amazing Award Flight Availability with Ease </title>
        <meta
          name="description"
          content="Discover the best award flight availability to maximize your points and miles with AwardTool.com. Our powerful search engine helps you find top deals across airlines, compare availability, and book your dream trip with ease. Start saving thousands and hours today and unlock incredible travel rewards with expert tips and insights!"
        />
      </Helmet>
      <div className="max-w-[1120px] mx-auto relative">
        <img
          className={cn("w-full min-h-200 absolute top-[-2px]", { invisible: theme.isDarkMode })}
          src={lightBg}
        />

        <img
          className={cn("w-full min-h-200 absolute top-[-2px]", { invisible: !theme.isDarkMode })}
          src={darkBg}
        />

        <div className="text-center relative pt-20 mb-2 ml-1 mr-1 sm:pt-32 sm:mb-6 md:pt-52 md:mb-8 items-center justify-center">
          <Typography color="textPrimary" className="!text-2xl sm:!text-4xl md:!text-5xl">
            {tab === EnumTab.Hotel ? "Award Hotels" : "Award Flights"}
          </Typography>
          <div>
            <div
              className={
                isPc
                  ? "!text-1sm sm:!text-1sm md:!text-2sm pt-[0px] flex items-center justify-center"
                  : "!text-1sm sm:!text-1sm md:!text-2sm pt-[0px] flex items-center justify-center"
              }
            >
              <Typography
                color="textSecondary"
              // className=""
              >
                {tab === EnumTab.Hotel
                  ? "Elevate your award travel with award hotel"
                  : tab === EnumTab.Flight
                    ? "Multi-day, multi-city, and roundtrip"
                    : "Search by region with up to " + MAX_PANORAMA_SEARCH_DAYS + "-day range"}
              </Typography>
              {tab === EnumTab.Hotel ? null : (
                <Tooltip
                  className="inline"
                  sx={{ size: 10 }}
                  title={
                    tab === EnumTab.Flight
                      ? "Please enjoy our " +
                      FREE_FLIGHT_RANGE +
                      "-day search, " +
                      FREE_FLIGHT_RANGE +
                      " airport-pair search, or roundtrip search for free! PRO user enjoys " +
                      MAX_FLIGHT_RANGE +
                      "-entry search, which support up to " +
                      MAX_FLIGHT_RANGE +
                      "-day at one search!"
                      : "We monitor " +
                      ROUTE_NUMBER_COUNT.toLocaleString() +
                      "+ of program routes to maximize your flexibility in finding award tickets."
                  }
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 16,
                      marginLeft: "4px",
                      color: (theme) => theme.palette.text.secondary,
                    }}
                    onClick={() => {
                      setVisible(true);
                    }}
                  />
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        <div
          className={`flex justify-center ${isPc ? "mb-[20px] mt-[-12px]" : "px-[10px] mt-[10px]"}`}
        >
          <ToggleButtonGroup
            color="primary"
            value={tab}
            exclusive
            onChange={(_e, v: number) => {
              if (v) {
                if (v === EnumTab.Mega) {
                  navigate({ pathname: "/mega" });
                  return;
                }

                setTab(v);
                navigate({
                  pathname: "/",
                  search: createSearchParams({ tab: v + "" }).toString(),
                });
              }
            }}
            aria-label="Platform"
            size="small"
            sx={
              isPc
                ? {
                  width: "560px",
                }
                : {
                  width: "100%",
                  display: "flex",
                }
            }
          >
            <ToggleButton
              value={EnumTab.Flight}
              sx={{
                borderTopLeftRadius: "30px",
                borderBottomLeftRadius: "30px",
                flex: 1,
              }}
            >
              <div className="flex items-center px-[15px] space-x-[10px]">
                <FlightIcon sx={{ fontSize: "18px" }} className="rotate-[45deg]" />
                {/* {isPc && <span>Flight</span>} */}
                {isPc && <span>Flight</span>}
              </div>
            </ToggleButton>
            <ToggleButton
              value={EnumTab.Mega}
              sx={{
                borderTopRightRadius: "30px",
                borderBottomRightRadius: "30px",
                flex: 1,
              }}
            >
              <div className="flex items-center px-[15px] space-x-[10px]">
                <TravelExploreIcon sx={{ fontSize: "18px" }} />
                {isPc && <span>Mega</span>}
              </div>
            </ToggleButton>
            <ToggleButton
              value={EnumTab.Panorama}
              sx={{
                borderTopRightRadius: "30px",
                borderBottomRightRadius: "30px",
                flex: 1,
              }}
            >
              <div className="flex items-center px-[15px] space-x-[10px]">
                <StarPurple500Icon sx={{ fontSize: "18px" }} />
                {/* {isPc && <span>Panorama</span>} */}
                {isPc && <span>Panorama</span>}
              </div>
            </ToggleButton>

            <ToggleButton
              value={EnumTab.Hotel}
              sx={{
                borderTopRightRadius: "30px",
                borderBottomRightRadius: "30px",
                flex: 1,
              }}
            >
              <div className="flex items-center px-[15px]  space-x-[10px]">
                <HotelIcon sx={{ fontSize: "18px" }} />
                {isPc && <span>Hotel</span>}
              </div>
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
        <SearchBox>
          <div
            className={
              tab === EnumTab.Flight
                ? "min-h-[90px] flex flex-row items-center"
                : "min-h-[120px] flex flex-row items-center"
            }
          >
            <div className="w-full">
              {tab === EnumTab.Flight && <SearchBar onChange={handleChange} />}
              {tab === EnumTab.Panorama && (
                <RouteSearchBar searchBtn={false} onChange={handleRoutesChange} useWhere="home" />
              )}

              {tab === EnumTab.Hotel && (
                <div>
                  <div className="h-[25px]" />
                  <HotelSearchBar onSearch={() => { }} onSearchAll={() => { }} useWhere="home" />
                </div>
              )}
            </div>
          </div>

          <div className="absolute bottom-0 left-1/2 translate-y-1/2 -translate-x-1/2">
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              sx={{
                borderRadius: 20,
                // padding: "10px 24px",
                fontSize: "0.95rem",
                // minWidth: "150px"
              }}
              onClick={() => {
                if (tab === EnumTab.Flight) {
                  search();
                } else if (tab === EnumTab.Panorama) {
                  searchRoute();
                } else if (tab === EnumTab.Hotel) {
                  searchHotel();
                }
              }}
            >
              Search
            </Button>
          </div>
        </SearchBox>
        {/* {isPc && <HightLights />} */}
        <RoutesRecommend ref={recommendRef} />
        {<HightLights />}

        <HomeBanner />
        {/* <FeedbackBar /> */}
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: "12px",
            },
          }}
        >
          <DialogContent>
            <div className="flex flex-col w-full items-center justify-center py-[10px]">
              <Typography variant="body2" className="!text-[38px] pb-[3px]">
                😃
              </Typography>
              <Typography variant="body2" className="!text-[18px] pb-[3px] text-center">
                Congratulations for being PRO user！🥳🎉👏
              </Typography>
              <Typography variant="body2" className="!text-[18px] pb-[13px] text-center">
                {"Thanks for choosing " +
                  siteName +
                  ".com for your award travel planning! Truly Appreciate it!"}
              </Typography>
              <Typography variant="body2" className="!text-[15px] pb-[10px]">
                You might need to refresh page to get the PRO features.
              </Typography>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog
          open={visible}
          onClose={() => setVisible(false)}
          PaperProps={{
            sx: {
              borderRadius: "12px",
            },
          }}
        >
          {tab === EnumTab.Flight ? (
            <DialogContent>
              <Typography color="primary" fontSize={16}>
                Multiday, multicity, and roundtrip search
              </Typography>
              <Typography fontSize={16}>
                {"Please enjoy our free roundtrip, " +
                  FREE_FLIGHT_RANGE +
                  "-day search, and " +
                  FREE_FLIGHT_RANGE +
                  " airport-pair search!"}
              </Typography>
              <Typography fontSize={16}>
                {"PRO user enjoys " +
                  MAX_FLIGHT_RANGE +
                  "-entry search, which support up to " +
                  MAX_FLIGHT_RANGE +
                  "-day at one search!"}
              </Typography>
              {/* <Typography color="primary" fontSize={16}>
              PRO
            </Typography>
            <Typography color="primary" fontSize={16}>
              {" "}
              user can search up to 12-day and 12-airport-pair.
            </Typography> */}
            </DialogContent>
          ) : (
            <DialogContent>
              <Typography color="primary" fontSize={16}>
                {"Search by region with up to " + MAX_PANORAMA_SEARCH_DAYS + "-day range"}
              </Typography>
              <Typography fontSize={16}>{"Search with unlimited airports and regions."}</Typography>
              <Typography fontSize={16}>
                {"Enjoying a complimentary 30-day range search within a 90-day timeframe."}
              </Typography>
              <Typography fontSize={16}>
                {"Upgrade to PRO to enjoy 365 days of data and search with " +
                  MAX_PANORAMA_SEARCH_DAYS +
                  "-day range."}
              </Typography>
              {/* <Typography color="primary" fontSize={16}>
              PRO
            </Typography>
            <Typography color="primary" fontSize={16}>
              {" "}
              user can search up to 12-day and 12-airport-pair.
            </Typography> */}
            </DialogContent>
          )}
          <DialogActions>
            <Button onClick={() => setVisible(false)}>OK</Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default Home;
